import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import TierPaymentModal from '@/components/TierPaymentModal';
import { Card, CardContent } from '@/components/ui/card';
import { Lock, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { convertEventTime, getUserTimezone, sortEventsByTime } from '@/utils/timezoneUtils';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '@/styles/calendar-grid.css';

const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Custom event component for displaying in calendar cells
const EventComponent = ({ event }) => {
  const isPast = event.start < new Date();
  
  return (
    <div 
      className={`rbc-event-content ${isPast ? 'past-event' : 'upcoming-event'}`}
      title={`${event.title} - ${event.resource.timeDisplay || format(event.start, 'h:mm a')}`}
    >
      <div className="event-title">{event.title}</div>
      <div className="event-time">{event.resource.timeDisplay || format(event.start, 'h:mm a')}</div>
    </div>
  );
};

// Custom toolbar for navigation
const CustomToolbar = ({ date, onNavigate }) => {
  return (
    <div className="rbc-toolbar">
      <div className="rbc-btn-group">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate('PREV')}
          className="bg-white hover:bg-gray-50"
          aria-label="Previous month"
          data-testid="calendar-prev-month"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate('TODAY')}
          className="bg-white hover:bg-gray-50"
          data-testid="calendar-today"
        >
          Today
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate('NEXT')}
          className="bg-white hover:bg-gray-50"
          aria-label="Next month"
          data-testid="calendar-next-month"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      <span className="rbc-toolbar-label text-2xl font-bold text-cyan-500">
        {format(date, 'MMMM yyyy')}
      </span>
      <div className="rbc-btn-group"></div>
    </div>
  );
};

// Day cell wrapper to control clickability
const DayWrapper = ({ children, value, calendarEvents }) => {
  const dateStr = format(value, 'yyyy-MM-dd');
  const dayEvents = calendarEvents.filter(event => 
    format(event.start, 'yyyy-MM-dd') === dateStr
  );
  const hasEvents = dayEvents.length > 0;

  return (
    <div 
      className={`rbc-day-bg ${hasEvents ? 'has-events' : 'no-events'}`}
      style={{ cursor: hasEvents ? 'pointer' : 'default' }}
    >
      {children}
    </div>
  );
};

const CalendarGridPage = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membershipType, setMembershipType] = useState(user?.membership_tier || null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
    fetchMembershipStatus();
  }, []);

  const fetchMembershipStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/membership`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembershipType(response.data.membership_type);
    } catch (error) {
      console.error('Failed to fetch membership status');
    }
  };

  const isViewOnly = membershipType !== null && membershipType === 'free';

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/calendar/events?per_page=500`);
      setEvents(response.data.events || response.data || []);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  // Transform backend events to calendar format with timezone conversion
  const calendarEvents = useMemo(() => {
    const userTimezone = getUserTimezone();
    
    return sortEventsByTime(events).map(event => {
      const eventTimezone = event.timezone || 'America/New_York';
      const converted = convertEventTime(
        event.event_date,
        event.event_time,
        eventTimezone,
        userTimezone
      );
      
      const endDate = new Date(converted.date);
      endDate.setHours(endDate.getHours() + 1);

      return {
        id: event.id,
        title: event.title,
        start: converted.date,
        end: endDate,
        allDay: false,
        resource: {
          ...event,
          timeDisplay: converted.displayTime,
          fullTimeDisplay: converted.fullDisplay
        },
      };
    });
  }, [events]);

  // Handle clicking on a day with events
  const handleSelectEvent = useCallback((event) => {
    const dateStr = format(event.start, 'yyyy-MM-dd');
    navigate(`/dashboard/calendar/${dateStr}`);
  }, [navigate]);

  // Handle clicking on a day slot
  const handleSelectSlot = useCallback((slotInfo) => {
    // Find events on this day
    const dayEvents = calendarEvents.filter(event => 
      format(event.start, 'yyyy-MM-dd') === format(slotInfo.start, 'yyyy-MM-dd')
    );

    if (dayEvents.length > 0) {
      const dateStr = format(slotInfo.start, 'yyyy-MM-dd');
      navigate(`/dashboard/calendar/${dateStr}`);
    }
  }, [calendarEvents, navigate]);

  // Create a wrapper for DayWrapper that has access to calendarEvents
  const DayWrapperWithEvents = useCallback((props) => (
    <DayWrapper {...props} calendarEvents={calendarEvents} />
  ), [calendarEvents]);

  if (loading) {
    return <div className="text-center py-20">Loading calendar...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">Calendar</h1>
        <p className="text-lg text-gray-300">View all upcoming sessions and events</p>
      </div>

      {/* Membership Required Banner for Free Users */}
      {isViewOnly && (
        <Card 
          className="mb-6 border-2 border-yellow-500 bg-yellow-50 cursor-pointer hover:border-yellow-600 transition-all"
          onClick={() => setShowPaymentModal(true)}
        >
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Lock className="w-6 h-6 text-yellow-600" />
              <div>
                <h3 className="font-semibold text-yellow-900">View-Only Mode - Click to Upgrade</h3>
                <p className="text-sm text-yellow-800">
                  Upgrade your membership to access live sessions and interactive features. You can still browse all upcoming events.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar Grid */}
      <Card className="bg-white p-4 sm:p-6" style={{ opacity: 1 }}>
        <CardContent className="p-0">
          <div className="calendar-container" style={{ height: '700px' }}>
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              selectable
              views={['month']}
              defaultView="month"
              date={currentDate}
              onNavigate={(date) => setCurrentDate(date)}
              components={{
                toolbar: CustomToolbar,
                event: EventComponent,
                dateCellWrapper: DayWrapperWithEvents,
              }}
              eventPropGetter={(event) => {
                const isPast = event.start < new Date();
                return {
                  className: isPast ? 'past-event-wrapper' : 'upcoming-event-wrapper',
                };
              }}
            />
          </div>
        </CardContent>
      </Card>

      {events.length === 0 && (
        <Card className="bg-white mt-6" style={{ opacity: 1 }}>
          <CardContent className="py-12 text-center text-gray-500">
            <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No events scheduled yet. Check back soon!</p>
          </CardContent>
        </Card>
      )}

      <TierPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currentTier={user?.membership_tier || 'bronze'}
      />
    </div>
  );
};

export default CalendarGridPage;
